/**
 * Utility functions for capturing and storing UTM parameters and referrer data.
 * Used for tracking marketing attribution across the signup flow and other analytics.
 */

/**
 * Interface for tracking referrer and UTM data
 */
export interface ReferrerData {
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
}

/**
 * Shape of the cross-domain `cirra_attribution` cookie set by cirra.ai,
 * docs.cirra.ai, and skills.cirra.ai on the `.cirra.ai` apex. It survives the
 * marketing-site → app hop that overwrites `document.referrer`, and preserves
 * first-touch `referrer` + last-touch UTMs/click IDs captured before the user
 * ever reached app.cirra.ai.
 */
interface CirraAttributionCookie {
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  li_fat_id?: string;
  ttclid?: string;
  landing_page?: string;
  first_touch_at?: string;
  last_touch_at?: string;
}

/**
 * Get UTM parameters and ad click IDs (gclid) from the current URL
 */
export const getUTMParameters = (): Partial<ReferrerData> => {
  const params = new URLSearchParams(window.location.search);
  const utmData: Partial<ReferrerData> = {};

  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const utmTerm = params.get('utm_term');
  const utmContent = params.get('utm_content');
  const gclid = params.get('gclid');

  if (utmSource) utmData.utmSource = utmSource;
  if (utmMedium) utmData.utmMedium = utmMedium;
  if (utmCampaign) utmData.utmCampaign = utmCampaign;
  if (utmTerm) utmData.utmTerm = utmTerm;
  if (utmContent) utmData.utmContent = utmContent;
  if (gclid) utmData.gclid = gclid;

  return utmData;
};

/**
 * Returns true for any referrer we should NOT record as an external traffic
 * source. Covers empty/missing values, any URL on the cirra.ai apex, and
 * referrers with opaque or non-web schemes (`about:blank`, `file:`, extension
 * protocols) whose `origin` serializes to the string `"null"` and whose
 * `hostname` is empty. Only `http:` / `https:` URLs with a non-empty hostname
 * are considered real external sources.
 */
const isInternalReferrer = (ref: string | undefined | null): boolean => {
  if (!ref) return true;
  try {
    const url = new URL(ref);
    if (
      (url.protocol !== 'http:' && url.protocol !== 'https:') ||
      !url.hostname
    ) {
      return true;
    }
    return /(^|\.)cirra\.ai$/i.test(url.hostname);
  } catch {
    return true;
  }
};

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined' || !document.cookie) return undefined;
  const escaped = name.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  // The space after `;` in cookie strings is convention, not guaranteed by
  // spec — allow any whitespace so an unconventional serializer doesn't cause
  // us to miss the cookie and reset first-touch data.
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)')
  );
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const readAttributionCookie = (): CirraAttributionCookie | null => {
  const raw = readCookie('cirra_attribution');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Reject non-plain-object payloads (including arrays — `typeof [] === 'object'`)
    // so a tampered or corrupted cookie doesn't feed a malformed shape downstream.
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return null;
    }
    return parsed as CirraAttributionCookie;
  } catch {
    return null;
  }
};

/**
 * Project cookie fields onto the app's internal `ReferrerData` shape.
 * Cookie uses snake_case (standard for web tracking); internal type is
 * camelCase. Only non-empty fields are copied so downstream callers don't
 * see spurious blank values.
 */
const cookieToReferrerData = (cookie: CirraAttributionCookie): ReferrerData => {
  const out: ReferrerData = {};
  if (cookie.referrer && !isInternalReferrer(cookie.referrer)) {
    out.referrer = cookie.referrer;
  }
  if (cookie.utm_source) out.utmSource = cookie.utm_source;
  if (cookie.utm_medium) out.utmMedium = cookie.utm_medium;
  if (cookie.utm_campaign) out.utmCampaign = cookie.utm_campaign;
  if (cookie.utm_term) out.utmTerm = cookie.utm_term;
  if (cookie.utm_content) out.utmContent = cookie.utm_content;
  if (cookie.gclid) out.gclid = cookie.gclid;
  return out;
};

/**
 * Resolve the best available external referrer.
 *
 * `document.referrer` on app.cirra.ai is overwritten to `https://cirra.ai/`
 * when the user navigates from the marketing site, destroying the original
 * traffic source. The `cirra_attribution` cookie is set on the `.cirra.ai`
 * apex by cirra.ai (and docs/skills) with the first-touch external referrer,
 * and that value survives the hop. We prefer it whenever it's available and
 * external; otherwise we fall back to `document.referrer` for users who land
 * directly on app.cirra.ai (e.g. from a Reddit post linking straight to
 * /sign-up).
 */
export const resolveExternalReferrer = (): string | undefined => {
  const cookie = readAttributionCookie();
  if (cookie?.referrer && !isInternalReferrer(cookie.referrer)) {
    return cookie.referrer;
  }

  const docRef = typeof document !== 'undefined' ? document.referrer : '';
  if (docRef && !isInternalReferrer(docRef)) return docRef;

  return undefined;
};

/**
 * Google Ads auto-tagging adds `gclid` to the URL but no `utm_source`/
 * `utm_medium`. Infer the standard `google`/`cpc` values in-place so
 * downstream destinations (Segment, Attio) can read a usable source without
 * decoding the opaque `gclid`. Explicit UTM values always win — this only
 * fills in when the UTM slot is empty.
 */
const inferGoogleAdsUtms = (data: ReferrerData): void => {
  if (data.gclid && !data.utmSource) {
    data.utmSource = 'google';
    if (!data.utmMedium) {
      data.utmMedium = 'cpc';
    }
  }
};

/**
 * Get and store referrer and UTM data for tracking
 * This should be called when the page loads to capture the initial referrer.
 *
 * Attribution model:
 * - Referrer: first-touch (the earliest external source we see wins)
 * - UTMs / click IDs: last-touch (a fresh campaign visit overwrites)
 *
 * Sources, in order of preference:
 *   1. `cirra_attribution` cookie (set by cirra.ai / docs / skills)
 *   2. UTMs on the current URL (merged in as last-touch)
 *   3. `document.referrer` if external (for direct-to-app visits)
 */
export const captureReferrerData = (): ReferrerData => {
  const existingData = getStoredReferrerData();
  const currentUTMData = getUTMParameters();
  const cookie = readAttributionCookie();
  const cookieData = cookie ? cookieToReferrerData(cookie) : {};

  const hasCurrentUTMParams = Object.keys(currentUTMData).length > 0;
  const hasExistingMeaningfulData =
    !!existingData && Object.keys(existingData).length > 0;

  // Preserve previously stored data (e.g. across the email-verification tab
  // hop) unless a fresh tracked visit arrives or we have a better external
  // referrer than the one we saved last time.
  if (hasExistingMeaningfulData && !hasCurrentUTMParams) {
    const existingReferrerIsInternal = isInternalReferrer(
      existingData.referrer
    );
    const existingHasCampaign =
      !!existingData.utmSource ||
      !!existingData.utmMedium ||
      !!existingData.utmCampaign ||
      !!existingData.utmTerm ||
      !!existingData.utmContent ||
      !!existingData.gclid;

    // Upgrade referrer only when we have a real external replacement for the
    // internal (cirra.ai) value currently stored. Without an external
    // fallback we'd just delete the referrer and be left with an empty
    // entry, triggering staleness issues downstream.
    const externalReferrerFallback =
      cookieData.referrer ??
      (document.referrer && !isInternalReferrer(document.referrer)
        ? document.referrer
        : undefined);
    const canUpgradeReferrer =
      existingReferrerIsInternal && !!externalReferrerFallback;
    // Fill the campaign block from the cookie only when the existing data
    // had no campaign AND the cookie actually carries one. Gating on `!!cookie`
    // alone would run the upgrade path for cookies that only carry metadata
    // (landing_page / first_touch_at) with nothing to adopt, potentially
    // producing an empty upgraded object that diverges from localStorage.
    const cookieHasCampaign =
      !!cookieData.utmSource ||
      !!cookieData.utmMedium ||
      !!cookieData.utmCampaign ||
      !!cookieData.utmTerm ||
      !!cookieData.utmContent ||
      !!cookieData.gclid;
    const canAdoptCookieCampaign = !existingHasCampaign && cookieHasCampaign;

    if (!canUpgradeReferrer && !canAdoptCookieCampaign) {
      return existingData;
    }

    const upgraded: ReferrerData = { ...existingData };
    if (canAdoptCookieCampaign) {
      if (cookieData.utmSource) upgraded.utmSource = cookieData.utmSource;
      if (cookieData.utmMedium) upgraded.utmMedium = cookieData.utmMedium;
      if (cookieData.utmCampaign) upgraded.utmCampaign = cookieData.utmCampaign;
      if (cookieData.utmTerm) upgraded.utmTerm = cookieData.utmTerm;
      if (cookieData.utmContent) upgraded.utmContent = cookieData.utmContent;
      if (cookieData.gclid) upgraded.gclid = cookieData.gclid;
      // Cookie may have only gclid (Google Ads auto-tagging) — infer the
      // standard utm_source/utm_medium just like the fresh-capture path does,
      // so Segment/Attio see proper source dimensions instead of a bare gclid.
      inferGoogleAdsUtms(upgraded);
    }
    if (canUpgradeReferrer) {
      upgraded.referrer = externalReferrerFallback;
    }

    // Symmetric with the fresh-capture path: if we somehow end up with no
    // fields at all, clear the stale localStorage entry instead of leaving a
    // value that diverges from what this function returned.
    if (Object.keys(upgraded).length > 0) {
      localStorage.setItem('signup_referrer_data', JSON.stringify(upgraded));
    } else {
      localStorage.removeItem('signup_referrer_data');
    }
    return upgraded;
  }

  // Fresh capture — either no prior data, or the current URL carries UTMs
  // (which means this is a new campaign touch and should overwrite the
  // campaign block).
  const externalReferrer = resolveExternalReferrer();
  // Strip referrer from cookieData — external referrer resolution handles it
  // separately and has its own cross-source precedence.
  const { referrer: _cookieReferrer, ...cookieCampaign } = cookieData;
  void _cookieReferrer;
  // Last-touch campaign model: if the URL carries *any* campaign param, it
  // represents a full fresh touch and its values fully replace the cookie's
  // campaign block. Otherwise the cookie's campaign carries forward. Never
  // merge fields from two different campaign touches — that silently mixes
  // utm_source from one visit with utm_campaign from another.
  const campaignData: ReferrerData = hasCurrentUTMParams
    ? currentUTMData
    : cookieCampaign;
  // Referrer is first-touch sticky — if localStorage already records an
  // external referrer from an earlier page-load, keep it even when a fresh
  // UTM'd URL triggers this fresh-capture path. Last-touch UTMs on top of
  // a first-touch referrer is the intended attribution model; letting the
  // resolved `externalReferrer` (which is only the current-visit view) win
  // here would silently overwrite — or drop — the original source.
  const existingReferrer = existingData?.referrer;
  const existingReferrerIsExternal =
    !!existingReferrer && !isInternalReferrer(existingReferrer);
  const stickyReferrer = existingReferrerIsExternal
    ? existingReferrer
    : externalReferrer;
  const referrerData: ReferrerData = {
    ...(stickyReferrer ? { referrer: stickyReferrer } : {}),
    ...campaignData,
  };

  inferGoogleAdsUtms(referrerData);

  // Store in localStorage so it persists across tabs (email verification opens in a new tab)
  // Only store if we have meaningful data; avoid persisting empty objects
  if (Object.keys(referrerData).length > 0) {
    localStorage.setItem('signup_referrer_data', JSON.stringify(referrerData));
  } else {
    localStorage.removeItem('signup_referrer_data');
  }

  return referrerData;
};

/**
 * Get stored referrer data from localStorage
 */
export const getStoredReferrerData = (): ReferrerData | null => {
  const stored = localStorage.getItem('signup_referrer_data');
  if (stored) {
    try {
      return JSON.parse(stored) as ReferrerData;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Build Auth0 authorization params from referrer data.
 * These parameters are passed to Auth0's /authorize endpoint and can be
 * accessed in Auth0 Actions via event.request.query.
 *
 * @param data - ReferrerData object (or null)
 * @returns Object with utm_* and referrer params to spread into authorizationParams
 */
export const buildAuth0UTMParams = (
  data: ReferrerData | null | undefined
): Record<string, string> => {
  if (!data) return {};

  const params: Record<string, string> = {};

  // Note: gclid → utm_source/utm_medium inference is handled upstream by
  // captureReferrerData() when storing to localStorage. By the time data
  // reaches this function, utmSource/utmMedium are already set if gclid
  // was present, so no duplicate inference is needed here.

  if (data.utmSource) params.utm_source = data.utmSource;
  if (data.utmMedium) params.utm_medium = data.utmMedium;
  if (data.utmCampaign) params.utm_campaign = data.utmCampaign;
  if (data.utmTerm) params.utm_term = data.utmTerm;
  if (data.utmContent) params.utm_content = data.utmContent;
  if (data.referrer) params.referrer = data.referrer;
  if (data.gclid) params.gclid = data.gclid;

  return params;
};
